import React from 'react';
import { useDispatch } from 'react-redux';
import { InterPoint } from '@saviynt/common';
import {
  Avatar,
  Box,
  ButtonIcon,
  Icon,
  Typography,
} from '@saviynt/design-system';
import { format, isValid } from 'date-fns';

import {
  CF_NEU_BLD,
  CF_NEU_STL,
  LIST_ACTIONS,
} from '../../../../../shared/constants';
import {
  useInvertedLocalize,
  useTypedSelector,
} from '../../../../../shared/hooks';
import { type ViolationMitigatingControls } from '../../../data-layer/detailsTypes';
import { detailsActions } from '../../../data-store/detailsSlice';

/**
 * MitigateCenterContent
 */
function MitigateCenterContent({
  row,
  globalDateFormat,
}: {
  row: ViolationMitigatingControls;
  globalDateFormat: string;
}) {
  const l = useInvertedLocalize();
  const { approverName, startDate, endDate, name } = row;

  const getInitials = approverName
    ? approverName
        .split(' ')
        .filter(Boolean)
        .map((part, i, arr) => {
          const cleanPart =
            i === arr.length - 1
              ? part.replace(/[(){}]/g, '') // remove brackets from final word
              : part;

          return i === 0 || i === arr.length - 1 ? cleanPart[0] : '';
        })
        .join('')
        .toUpperCase()
    : '';

  return (
    <Box className='Sod-ViolationsTabDetailsMitigating-centerContent'>
      {/* Upper */}
      <Box className='Sod-ViolationsTabDetailsMitigating-rowUpper'>
        <Typography kind='h3' lineLimit={1}>
          {name}
        </Typography>
      </Box>
      {/* Lower */}
      <Box className='Sod-ViolationsTabDetailsMitigating-rowLower'>
        {/* Start */}
        <Box className='Sod-ViolationsTabDetailsMitigating-inlineRowText'>
          <Typography kind='body2' color={CF_NEU_STL} isNoWrap>
            {l('Start:')}
          </Typography>
          <Typography kind='body2' color={CF_NEU_BLD} isNoWrap>
            {isValid(new Date(startDate))
              ? format(new Date(startDate), globalDateFormat)
              : '-'}
          </Typography>
        </Box>
        {/* End */}
        <InterPoint color='neutralBold' font='body2' />
        <Box className='Sod-ViolationsTabDetailsMitigating-inlineRowText'>
          <Typography kind='body2' color={CF_NEU_STL} isNoWrap>
            {l('End:')}
          </Typography>
          <Typography kind='body2' color={CF_NEU_BLD} isNoWrap>
            {isValid(new Date(endDate))
              ? format(new Date(endDate), globalDateFormat)
              : '-'}
          </Typography>
        </Box>
        {/* Mitigated By */}
        {approverName && (
          <>
            <InterPoint color='neutralBold' font='body2' />
            <Box className='Sod-ViolationsTabDetailsMitigating-inlineRowText'>
              <Typography kind='body2' color={CF_NEU_STL} isNoWrap>
                {l('Added by:')}
              </Typography>
              <Avatar size='xxSmall'>{getInitials}</Avatar>
              <Typography kind='body2' color={CF_NEU_BLD} isNoWrap>
                {approverName}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}

/**
 * MitigateTralingContent
 */
function MitigateTralingContent({
  mcKey,
  isDisabled,
  actionsList,
}: {
  mcKey: number;
  isDisabled: boolean;
  actionsList: string[];
}) {
  const l = useInvertedLocalize();
  const dispatch = useDispatch();
  const { actionManager } = useTypedSelector(
    (state) => state.aag.details.violationsTab
  );

  return (
    <Box className='Sod-ViolationsTabDetailsMitigating-trailing'>
      <ButtonIcon
        size='large'
        kind='outlinedSecondary'
        ariaLabel={l('Extend end date')}
        icon={<Icon kind='ExtendEndDate' />}
        isDisabled={
          isDisabled ||
          !actionsList.includes(LIST_ACTIONS.CHNAGEMCENDDATE.value)
        }
        onClick={() => {
          dispatch(
            detailsActions.setViolationTabdetailsActionManager({
              ...actionManager,
              isOpen: true,
              sodKey: actionManager?.sodKey ?? '',
              action: LIST_ACTIONS.CHNAGEMCENDDATE.value,
              mcKeys: [mcKey],
            })
          );
        }}
      />
      <ButtonIcon
        size='large'
        kind='outlinedSecondary'
        ariaLabel={l('Delete')}
        icon={<Icon kind='Delete' />}
        isDisabled={
          isDisabled || !actionsList.includes(LIST_ACTIONS.REMOVEMC.value)
        }
        onClick={() => {
          dispatch(
            detailsActions.setViolationTabdetailsActionManager({
              ...actionManager,
              isOpen: true,
              sodKey: actionManager?.sodKey ?? '',
              action: LIST_ACTIONS.REMOVEMC.value,
              mcKeys: [mcKey],
            })
          );
        }}
      />
    </Box>
  );
}

export { MitigateCenterContent, MitigateTralingContent };
